import React from 'react';
import { Helmet } from 'react-helmet-async';
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const PrivacyPolicy = () => {
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Privacy Policy - Tagnetica</title>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="googlebot" content="noindex, nofollow" />
        <meta name="bingbot" content="noindex, nofollow" />
        <meta name="description" content="Privacy Policy for Tagnetica" />
      </Helmet>
      
      <Header />
      
      <main className="pt-16">
        <div className="container mx-auto px-6 lg:px-8 py-12">
          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="mb-12">
              <h1 className="text-4xl font-bold text-text-primary mb-6">Privacy Policy</h1>
              <p className="text-lg text-text-secondary">
                Last updated: {new Date().toLocaleDateString('en-GB', { 
                  day: 'numeric', 
                  month: 'long', 
                  year: 'numeric' 
                })}
              </p>
            </div>

            {/* Table of Contents */}
            <div className="bg-accent/30 border border-primary/20 rounded-xl p-8 mb-12">
              <h2 className="text-2xl font-semibold text-text-primary mb-6">Table of Contents</h2>
              <nav className="space-y-2">
                <button 
                  onClick={() => scrollToSection('introduction')}
                  className="block text-left text-primary hover:text-primary/80 underline transition-colors"
                >
                  1. Introduction & Scope
                </button>
                <button 
                  onClick={() => scrollToSection('data-collection')}
                  className="block text-left text-primary hover:text-primary/80 underline transition-colors"
                >
                  2. Data Collection
                </button>
                <button 
                  onClick={() => scrollToSection('methods')}
                  className="block text-left text-primary hover:text-primary/80 underline transition-colors"
                >
                  3. Methods of Collection
                </button>
                <button 
                  onClick={() => scrollToSection('purposes')}
                  className="block text-left text-primary hover:text-primary/80 underline transition-colors"
                >
                  4. Purposes & Legal Bases
                </button>
                <button 
                  onClick={() => scrollToSection('sharing')}
                  className="block text-left text-primary hover:text-primary/80 underline transition-colors"
                >
                  5. Data Sharing & Disclosure
                </button>
                <button 
                  onClick={() => scrollToSection('retention')}
                  className="block text-left text-primary hover:text-primary/80 underline transition-colors"
                >
                  6. Retention & Deletion
                </button>
                <button 
                  onClick={() => scrollToSection('security')}
                  className="block text-left text-primary hover:text-primary/80 underline transition-colors"
                >
                  7. Security Measures
                </button>
                <button 
                  onClick={() => scrollToSection('rights')}
                  className="block text-left text-primary hover:text-primary/80 underline transition-colors"
                >
                  8. User Rights & How to Exercise Them
                </button>
                <button 
                  onClick={() => scrollToSection('cookies')}
                  className="block text-left text-primary hover:text-primary/80 underline transition-colors"
                >
                  9. Cookie Banner & Preferences
                </button>
                <button 
                  onClick={() => scrollToSection('updates')}
                  className="block text-left text-primary hover:text-primary/80 underline transition-colors"
                >
                  10. Updates & Changes
                </button>
              </nav>
            </div>

            {/* Content Sections */}
            <div className="space-y-12">
              {/* 1. Introduction & Scope */}
              <section id="introduction">
                <h2 className="text-3xl font-semibold text-text-primary mb-6">1. Introduction & Scope</h2>
                <div className="prose prose-lg max-w-none text-text-secondary space-y-4">
                  <p>
                    <strong>Tagnetica</strong> ("we," "our," or "us") is a UK-based analytics and tag management consulting service. 
                    We help businesses implement and optimize their digital analytics and tracking systems.
                  </p>
                  <p>
                    <strong>Contact Information:</strong><br/>
                    Email: hello@tagnetica.co.uk<br/>
                    Website: https://tagimplementation.tagnetica.co.uk
                  </p>
                  <p>
                    <strong>Data Controller:</strong> Tagnetica acts as the data controller for personal data collected through our website 
                    and services. We are responsible for determining how and why your personal data is processed.
                  </p>
                  <p>
                    This Privacy Policy explains how we collect, use, disclose, and protect your personal information when you visit our website, 
                    use our services, or interact with us. It applies to users in the European Union (EU), United Kingdom (UK), and California, 
                    and complies with applicable data protection laws including GDPR and CCPA.
                  </p>
                </div>
              </section>

              {/* 2. Data Collection */}
              <section id="data-collection">
                <h2 className="text-3xl font-semibold text-text-primary mb-6">2. Data Collection</h2>
                <div className="prose prose-lg max-w-none text-text-secondary space-y-6">
                  <div>
                    <h3 className="text-xl font-semibold text-text-primary mb-3">Personal Data</h3>
                    <p>We may collect the following types of personal information:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li><strong>Contact Information:</strong> Name, email address, phone number</li>
                      <li><strong>Business Information:</strong> Company name, job title, website URL</li>
                      <li><strong>Billing Information:</strong> Payment details (processed securely through third-party providers)</li>
                      <li><strong>Communication Data:</strong> Messages, feedback, and correspondence with us</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h3 className="text-xl font-semibold text-text-primary mb-3">Usage Data</h3>
                    <p>We automatically collect certain information when you visit our website:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li><strong>Technical Data:</strong> IP address, browser type and version, device information</li>
                      <li><strong>Usage Patterns:</strong> Pages visited, time spent on pages, click patterns</li>
                      <li><strong>Analytics Data:</strong> Traffic sources, user behavior, conversion tracking</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-text-primary mb-3">Cookies & Tracking Technologies</h3>
                    <p>
                      We use cookies and similar tracking technologies to enhance your experience and analyze website usage. 
                      These may include:
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li><strong>Essential Cookies:</strong> Required for basic website functionality</li>
                      <li><strong>Analytics Cookies:</strong> Help us understand how visitors use our site</li>
                      <li><strong>Marketing Cookies:</strong> Used for targeted advertising and content</li>
                    </ul>
                    <p>
                      You can manage your cookie preferences through our cookie settings center or your browser settings.
                    </p>
                  </div>
                </div>
              </section>

              {/* 3. Methods of Collection */}
              <section id="methods">
                <h2 className="text-3xl font-semibold text-text-primary mb-6">3. Methods of Collection</h2>
                <div className="prose prose-lg max-w-none text-text-secondary space-y-4">
                  <div>
                    <h3 className="text-xl font-semibold text-text-primary mb-3">Direct Collection</h3>
                    <p>We collect information directly from you when you:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Fill out contact forms on our website</li>
                      <li>Book consultation calls or meetings</li>
                      <li>Subscribe to our newsletter or updates</li>
                      <li>Engage with our services or support</li>
                      <li>Communicate with us via email or phone</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-text-primary mb-3">Automated Collection</h3>
                    <p>We automatically collect certain information through:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Website analytics tools (Google Analytics, Matomo)</li>
                      <li>Tag management systems (Google Tag Manager)</li>
                      <li>Server logs and technical monitoring</li>
                      <li>Cookies and similar technologies</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-text-primary mb-3">Third-Party Sources</h3>
                    <p>We may receive information from:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Business partners and service providers</li>
                      <li>Public databases and directories</li>
                      <li>Social media platforms (when you interact with our content)</li>
                      <li>Referral sources and networking contacts</li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* 4. Purposes & Legal Bases */}
              <section id="purposes">
                <h2 className="text-3xl font-semibold text-text-primary mb-6">4. Purposes & Legal Bases</h2>
                <div className="prose prose-lg max-w-none text-text-secondary space-y-6">
                  <div>
                    <h3 className="text-xl font-semibold text-text-primary mb-3">Performance of Contract</h3>
                    <p>We process your data to:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Provide our analytics and tag management services</li>
                      <li>Fulfill contractual obligations and deliver results</li>
                      <li>Process payments and manage billing</li>
                      <li>Provide customer support and technical assistance</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-text-primary mb-3">Legitimate Interests</h3>
                    <p>We process data based on our legitimate interests to:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Improve our services and develop new features</li>
                      <li>Analyze website usage and optimize user experience</li>
                      <li>Prevent fraud and ensure security</li>
                      <li>Send relevant business communications and updates</li>
                      <li>Maintain and improve our business operations</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-text-primary mb-3">Consent</h3>
                    <p>We rely on your consent for:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Non-essential cookies and tracking technologies</li>
                      <li>Marketing communications and newsletters</li>
                      <li>Sharing data with third-party marketing partners</li>
                    </ul>
                    <p>You can withdraw your consent at any time by contacting us or using our opt-out mechanisms.</p>
                  </div>
                </div>
              </section>

              {/* 5. Data Sharing & Disclosure */}
              <section id="sharing">
                <h2 className="text-3xl font-semibold text-text-primary mb-6">5. Data Sharing & Disclosure</h2>
                <div className="prose prose-lg max-w-none text-text-secondary space-y-6">
                  <div>
                    <h3 className="text-xl font-semibold text-text-primary mb-3">Service Providers & Subprocessors</h3>
                    <p>We may share your data with trusted third-party service providers who help us:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Host our website and provide technical infrastructure</li>
                      <li>Process payments and manage billing</li>
                      <li>Send emails and manage communications</li>
                      <li>Analyze website usage and provide analytics</li>
                      <li>Provide customer support and project management tools</li>
                    </ul>
                    <p>These providers are contractually bound to protect your data and use it only for specified purposes.</p>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-text-primary mb-3">Business Transfers</h3>
                    <p>In the event of a merger, acquisition, or sale of assets, your personal data may be transferred to the new entity. 
                    We will notify you of any such changes and ensure your data continues to be protected.</p>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-text-primary mb-3">Legal Obligations</h3>
                    <p>We may disclose your data when required by law, including:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>To comply with legal processes or government requests</li>
                      <li>To protect our rights, property, or safety</li>
                      <li>To investigate potential violations of our terms or policies</li>
                      <li>To prevent fraud or security threats</li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* 6. Retention & Deletion */}
              <section id="retention">
                <h2 className="text-3xl font-semibold text-text-primary mb-6">6. Retention & Deletion</h2>
                <div className="prose prose-lg max-w-none text-text-secondary space-y-6">
                  <div>
                    <h3 className="text-xl font-semibold text-text-primary mb-3">Retention Periods</h3>
                    <p>We retain your personal data for the following periods:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li><strong>Contact Information:</strong> Up to 3 years after last interaction</li>
                      <li><strong>Service Data:</strong> Duration of service plus 2 years</li>
                      <li><strong>Analytics Data:</strong> Up to 24 months</li>
                      <li><strong>Billing Records:</strong> 7 years (for tax and accounting purposes)</li>
                      <li><strong>Marketing Data:</strong> Until you opt out or withdraw consent</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-text-primary mb-3">Your Rights</h3>
                    <p>You have the right to:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li><strong>Access:</strong> Request a copy of your personal data</li>
                      <li><strong>Correction:</strong> Update inaccurate or incomplete information</li>
                      <li><strong>Deletion:</strong> Request deletion of your personal data</li>
                      <li><strong>Portability:</strong> Receive your data in a structured, machine-readable format</li>
                      <li><strong>Restriction:</strong> Limit how we process your data</li>
                      <li><strong>Objection:</strong> Object to certain types of processing</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-text-primary mb-3">Opt-Out Mechanisms</h3>
                    <p>You can opt out of:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Marketing communications by clicking unsubscribe links</li>
                      <li>Analytics tracking through our cookie settings</li>
                      <li>Third-party data sharing through our privacy controls</li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* 7. Security Measures */}
              <section id="security">
                <h2 className="text-3xl font-semibold text-text-primary mb-6">7. Security Measures</h2>
                <div className="prose prose-lg max-w-none text-text-secondary space-y-4">
                  <p>We implement appropriate technical and organizational measures to protect your personal data:</p>
                  
                  <div>
                    <h3 className="text-xl font-semibold text-text-primary mb-3">Technical Security</h3>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Encryption of data in transit (HTTPS/TLS) and at rest</li>
                      <li>Regular security updates and vulnerability assessments</li>
                      <li>Secure hosting infrastructure with industry-standard protections</li>
                      <li>Multi-factor authentication for administrative access</li>
                      <li>Regular backups with encryption</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-text-primary mb-3">Organizational Security</h3>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Employee training on data protection and privacy</li>
                      <li>Access controls and role-based permissions</li>
                      <li>Regular security audits and compliance reviews</li>
                      <li>Incident response procedures and breach notification protocols</li>
                    </ul>
                  </div>

                  <p>
                    While we strive to protect your data, no method of transmission over the internet is 100% secure. 
                    We cannot guarantee absolute security but will notify you of any data breaches as required by law.
                  </p>
                </div>
              </section>

              {/* 8. User Rights & How to Exercise Them */}
              <section id="rights">
                <h2 className="text-3xl font-semibold text-text-primary mb-6">8. User Rights & How to Exercise Them</h2>
                <div className="prose prose-lg max-w-none text-text-secondary space-y-6">
                  <div>
                    <h3 className="text-xl font-semibold text-text-primary mb-3">GDPR Rights (EU/UK)</h3>
                    <p>Under GDPR, you have the following rights:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li><strong>Right of Access:</strong> Request information about what data we hold about you</li>
                      <li><strong>Right of Rectification:</strong> Correct inaccurate or incomplete data</li>
                      <li><strong>Right to Erasure:</strong> Request deletion of your personal data</li>
                      <li><strong>Right to Restrict Processing:</strong> Limit how we use your data</li>
                      <li><strong>Right to Data Portability:</strong> Receive your data in a portable format</li>
                      <li><strong>Right to Object:</strong> Object to certain types of processing</li>
                      <li><strong>Right to Withdraw Consent:</strong> Withdraw consent for consent-based processing</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-text-primary mb-3">CCPA Rights (California)</h3>
                    <p>Under CCPA, you have the following rights:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li><strong>Right to Know:</strong> Request disclosure of personal information collected</li>
                      <li><strong>Right to Delete:</strong> Request deletion of personal information</li>
                      <li><strong>Right to Opt-Out:</strong> Opt out of the sale of personal information</li>
                      <li><strong>Right to Non-Discrimination:</strong> Not be discriminated against for exercising your rights</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-text-primary mb-3">How to Exercise Your Rights</h3>
                    <p>To exercise any of these rights, please contact us:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li><strong>Email:</strong> hello@tagnetica.co.uk</li>
                      <li><strong>Subject Line:</strong> "Privacy Rights Request"</li>
                      <li><strong>Include:</strong> Your name, email address, and specific request</li>
                    </ul>
                    <p>
                      We will respond to your request within 30 days (or 45 days if additional time is needed). 
                      We may need to verify your identity before processing certain requests.
                    </p>
                  </div>
                </div>
              </section>

              {/* 9. Cookie Banner & Preferences */}
              <section id="cookies">
                <h2 className="text-3xl font-semibold text-text-primary mb-6">9. Cookie Banner & Preferences</h2>
                <div className="prose prose-lg max-w-none text-text-secondary space-y-4">
                  <p>
                    We use cookies and similar technologies to enhance your browsing experience and analyze website usage. 
                    Our cookie banner allows you to make informed choices about which cookies you accept.
                  </p>
                  
                  <div>
                    <h3 className="text-xl font-semibold text-text-primary mb-3">Cookie Categories</h3>
                    <ul className="list-disc pl-6 space-y-2">
                      <li><strong>Essential Cookies:</strong> Required for basic website functionality (always active)</li>
                      <li><strong>Analytics Cookies:</strong> Help us understand how visitors use our site</li>
                      <li><strong>Marketing Cookies:</strong> Used for targeted advertising and content</li>
                      <li><strong>Preference Cookies:</strong> Remember your settings and preferences</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-text-primary mb-3">Managing Cookie Preferences</h3>
                    <p>You can manage your cookie preferences through:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Our cookie settings center (accessible via the cookie banner)</li>
                      <li>Your browser settings (to block or delete cookies)</li>
                      <li>Third-party opt-out tools for specific services</li>
                    </ul>
                    <p>
                      <strong>Cookie Settings Center:</strong> Click the "Cookie Settings" link in our cookie banner 
                      to access granular controls for different types of cookies.
                    </p>
                  </div>
                </div>
              </section>

              {/* 10. Updates & Changes */}
              <section id="updates">
                <h2 className="text-3xl font-semibold text-text-primary mb-6">10. Updates & Changes</h2>
                <div className="prose prose-lg max-w-none text-text-secondary space-y-4">
                  <p>
                    We may update this Privacy Policy from time to time to reflect changes in our practices, 
                    technology, legal requirements, or other factors. When we make changes, we will:
                  </p>
                  
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Update the "Last updated" date at the top of this policy</li>
                    <li>Post the updated policy on our website</li>
                    <li>Notify you of material changes via email or website notice</li>
                    <li>Obtain your consent for any changes that require it</li>
                  </ul>

                  <p>
                    We encourage you to review this Privacy Policy periodically to stay informed about how we 
                    protect your information. Your continued use of our services after any changes indicates 
                    your acceptance of the updated policy.
                  </p>

                  <div className="bg-accent/30 border border-primary/20 rounded-xl p-6 mt-8">
                    <h3 className="text-xl font-semibold text-text-primary mb-3">Contact Us</h3>
                    <p className="text-text-secondary">
                      If you have any questions about this Privacy Policy or our data practices, please contact us:
                    </p>
                    <ul className="list-none space-y-2 mt-3">
                      <li><strong>Email:</strong> hello@tagnetica.co.uk</li>
                      <li><strong>Website:</strong> https://tagimplementation.tagnetica.co.uk</li>
                    </ul>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy; 